import React, { useEffect, useState } from 'react';
import { Select } from 'antd';
import type { SelectProps } from 'antd';
import client from '../../api/client';

export interface District {
  id: number;
  name_ru: string;
  name_kk: string | null;
}

let cache: District[] | null = null;
let inflight: Promise<District[]> | null = null;

/**
 * Районы города из БАЗЫ, а не из захардкоженного списка.
 *
 * ⚠️ ЗАЧЕМ: до 03.08.2026 в админке лежал список из 7 строк вида
 * «Аксай — мкр.1», а в таблице `districts` (и в профиле жителя) названия
 * другие — «1-й микрорайон». Ни одна строка не совпадала, поэтому таргетинг
 * новостей и рассылка об отключениях сравнивали разные написания и молча
 * уходили в никуда, а 13 районов города вообще были недоступны.
 *
 * Источник — `GET /api/v1/districts`, тот же канонический список, который
 * показывает приложение жителя. Значение = `name_ru`: рассылка сравнивает
 * `users.district` со строкой посимвольно, поэтому написание обязано
 * совпадать с тем, что клиент пишет в профиль.
 */
export function useDistricts() {
  const [districts, setDistricts] = useState<District[]>(cache || []);
  const [loading, setLoading] = useState(!cache);

  useEffect(() => {
    if (cache) return;
    if (!inflight) {
      inflight = client
        .get<District[]>('/api/v1/districts')
        .then(({ data }) => {
          cache = data;
          return data;
        })
        .finally(() => {
          inflight = null;
        });
    }
    let alive = true;
    inflight
      .then((data) => {
        if (alive) setDistricts(data);
      })
      .catch(() => {
        /* сеть отвалилась — оставляем пустой список, поле честно пустое */
      })
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, []);

  return { districts, loading };
}

type Props = Omit<SelectProps, 'options' | 'loading'> & {
  /** Текст, когда район не выбран (значение = «для всех»). */
  allLabel?: string;
};

/** Выбор района города. Значение — `name_ru` из справочника. */
const DistrictSelect: React.FC<Props> = ({ allLabel, placeholder, ...rest }) => {
  const { districts, loading } = useDistricts();

  return (
    <Select
      showSearch
      allowClear
      loading={loading}
      placeholder={placeholder ?? allLabel ?? 'Выберите район'}
      optionFilterProp="label"
      options={districts.map((d) => ({ value: d.name_ru, label: d.name_ru }))}
      notFoundContent={loading ? 'Загрузка…' : 'Районы не загрузились'}
      {...rest}
    />
  );
};

export default DistrictSelect;
